import { z } from 'zod';

import { isPrismaCuid, isRfc4122Uuid } from './phase5bTier1Ids';

/**
 * Phase 5b — "Identity Union" for physical PK/FK fields while Prisma may still emit `cuid()` and
 * seeded rows use legacy slugs. Tier-1 routes may refine further; Tier-2 ingress passes legacy
 * strings through for the API `entityIdentityMap` to resolve via Rosetta manifest or deterministic v5.
 *
 * Order: UUID-shaped and cuid-shaped ids match first; any non-empty string is accepted last.
 */
export const zTransitionId = z.union([
  z.string().refine((s) => isRfc4122Uuid(s.trim()), { message: 'Expected RFC 4122 UUID' }),
  z.string().refine((s) => isPrismaCuid(s), { message: 'Expected Prisma cuid' }),
  z.string().min(1, 'Non-empty id required'),
]);

const LEGACY_SYNTHETIC_ID_MESSAGE =
  'Legacy synthetic IDs (L-, T-, OCL-) and t-timestamp forms are no longer accepted. Omit id or send a valid UUID or cuid.';

/** True for server/client shortcuts we no longer allow on create bodies (Phase 5b hygiene). */
export function isLegacySyntheticPrimaryId(val: string): boolean {
  const s = val.trim();
  if (s.length === 0) return false;
  if (s.startsWith('L-') || s.startsWith('T-') || s.startsWith('OCL-')) return true;
  // Historical tenantRepository fallback: `t` + digits only (e.g. t1715123456789)
  if (/^t\d+$/.test(s)) return true;
  return false;
}

/**
 * Optional `id` on POST create payloads: omit/empty is OK; otherwise reject legacy timestamp-style
 * ids while still allowing RFC UUID, Cuid, and other non-empty strings (not strict UUID-only yet).
 */
export const zTransitionIdCreateOptional = z.preprocess(
  (raw) =>
    raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')
      ? undefined
      : String(raw).trim(),
  z
    .string()
    .min(1)
    .refine((val) => !isLegacySyntheticPrimaryId(val), { message: LEGACY_SYNTHETIC_ID_MESSAGE })
    .optional(),
);
